// Анализ PDF до оплаты: ориентация документа и пустые страницы.
//
// Ориентация — по первой странице, как PbReader решает Orientation.AUTO
// (layout.py::resolve_orientation): шире, чем выше → альбомная. Viewport
// PDF.js уже учитывает /Rotate.
//
// Пустая страница — та, на которой после рендера в маленький растр почти
// нет «чернил». Порог допускает несколько пикселей (номер страницы в
// колонтитуле), но не строку текста. Рендер — с intent "print", как у
// превью: аннотации и поля форм, которые попадут на бумагу, учитываются.

const RENDER_SIDE = 220; // длинная сторона растра, px
const INK_LEVEL = 235; // канал темнее этого — уже не белый фон
const MAX_INK_SHARE = 0.0002; // доля «чернильных» пикселей, ниже которой страница пустая
// Больше — пропускаем поиск пустых страниц: на киоске это десятки секунд
// фоновой работы ради редкого случая.
export const MAX_ANALYZED_PAGES = 200;

export function isLandscape({ width, height }) {
    return width > height;
}

// Считает пиксели, у которых хоть один канал заметно темнее белого
// (жёлтый маркер по яркости почти белый, по синему каналу — нет).
// Останавливается, как только превышен limit: ответ уже известен.
export function countInk(data, limit = Infinity) {
    let ink = 0;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] && Math.min(data[i], data[i + 1], data[i + 2]) < INK_LEVEL && ++ink > limit) break;
    }
    return ink;
}

async function isBlankPage(page, canvas) {
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: RENDER_SIDE / Math.max(base.width, base.height) });
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport, intent: "print" }).promise;
    const limit = Math.floor(canvas.width * canvas.height * MAX_INK_SHARE);
    return countInk(ctx.getImageData(0, 0, canvas.width, canvas.height).data, limit) <= limit;
}

// → { pageCount, landscape, blankPages }. blankPages: нет поля — не искали
// (findBlank: false, предупреждение выключено в админке), null — страниц
// больше MAX_ANALYZED_PAGES. isCancelled проверяется между страницами.
export async function analyzePdf(pdf, { findBlank = true, isCancelled = () => false, makeCanvas = () => document.createElement("canvas") } = {}) {
    const pageCount = pdf.numPages;
    const first = await pdf.getPage(1);
    const landscape = isLandscape(first.getViewport({ scale: 1 }));
    if (!findBlank) return { pageCount, landscape };
    if (pageCount > MAX_ANALYZED_PAGES) return { pageCount, landscape, blankPages: null };

    const canvas = makeCanvas();
    const blankPages = [];
    try {
        for (let number = 1; number <= pageCount; number++) {
            if (isCancelled()) throw new Error("Analysis cancelled");
            const page = number === 1 ? first : await pdf.getPage(number);
            try {
                if (await isBlankPage(page, canvas)) blankPages.push(number);
            } finally {
                try { page.cleanup?.(); } catch { /* PDF may already be destroyed. */ }
            }
        }
    } finally {
        canvas.width = canvas.height = 0;
    }
    return { pageCount, landscape, blankPages };
}
